import React, { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { TicketDialog } from "@/components/TicketDialog";
import { AiComposeTools } from "@/components/AiComposeTools";
import { AutoTranslateFR } from "@/components/AutoTranslateFR";
import { MessageSquare, Send, Loader2, Wrench, UserCircle2, LifeBuoy } from "lucide-react";
import { useI18n } from "@/i18n";

const time = (iso) => { try { return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

export const OrderChat = ({ orderId, role = "client", vehicle = "", clientLang = "", className = "" }) => {
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [assigned, setAssigned] = useState(null);
  const [eng, setEng] = useState({ title: "", picture: "" });
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const endRef = useRef(null);
  const lastCount = useRef(0);

  const load = useCallback(async (scroll = false) => {
    try {
      const { data } = await api.get(`/orders/${orderId}/messages`);
      setMessages(data.messages || []);
      setAssigned(data.assigned_admin_name || null);
      setEng({ title: data.assigned_admin_title || "", picture: data.assigned_admin_picture || "" });
      if (scroll || (data.messages || []).length !== lastCount.current) {
        lastCount.current = (data.messages || []).length;
        setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
      }
      api.post(`/orders/${orderId}/messages/read`)
        .then(() => window.dispatchEvent(new Event("notif-refresh")))
        .catch(() => {});
    } catch (e) { /* silent */ }
    finally { setLoaded(true); }
  }, [orderId]);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), 4000);
    return () => clearInterval(t);
  }, [load]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    setText("");
    try {
      await api.post(`/orders/${orderId}/messages`, { text: t });
      await load(true);
    } catch (e) { setText(t); }
    finally { setSending(false); }
  };

  return (
    <div className={`glass rounded-md p-6 ${className}`} data-testid="order-chat">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquare className="w-5 h-5 text-aurora-cyan" />
        <h3 className="font-display text-lg font-semibold uppercase tracking-tight">
          {role === "admin" ? t("chat.adminTitle") : t("chat.clientTitle")}
        </h3>
      </div>
      <p className="font-mono text-[10px] text-zinc-500 mb-4">
        {!assigned && (role === "admin" ? t("chat.adminHint") : t("chat.clientHint"))}
      </p>
      {assigned && (
        <div className="flex items-center gap-3 mb-4 p-2.5 rounded-sm bg-aurora-cyan/5 border border-aurora-cyan/20" data-testid="assigned-engineer">
          {eng.picture
            ? <img src={eng.picture} alt="" className="w-10 h-10 rounded-full border border-aurora-cyan/40 object-cover" />
            : <div className="w-10 h-10 rounded-full bg-aurora-cyan/15 grid place-items-center text-aurora-cyan"><UserCircle2 className="w-6 h-6" /></div>}
          <div className="min-w-0">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{t("chat.engineerInCharge")}</div>
            <div className="text-sm font-semibold text-white truncate" data-testid="assigned-engineer-name">{assigned}</div>
            {eng.title ? <div className="font-mono text-[10px] text-aurora-cyan truncate">{eng.title}</div> : null}
          </div>
        </div>
      )}

      <div className="space-y-3 max-h-80 overflow-y-auto pr-1 mb-4" data-testid="chat-messages">
        {!loaded ? (
          <div className="flex items-center gap-2 text-zinc-500 font-mono text-xs py-4"><Loader2 className="w-4 h-4 animate-spin" /> {t("chat.loading")}</div>
        ) : messages.length === 0 ? (
          <p className="text-zinc-500 text-sm py-3">{role === "admin" ? t("chat.adminEmpty") : t("chat.clientEmpty")}</p>
        ) : messages.map((m) => {
          const mine = (role === "admin" && m.sender_role === "admin") || (role === "client" && m.sender_role === "client");
          const isEng = m.sender_role === "admin";
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] rounded-lg px-3.5 py-2.5 ${mine ? "bg-aurora-cyan/15 border border-aurora-cyan/30" : "bg-white/5 border border-white/10"}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  {isEng ? <Wrench className="w-3 h-3 text-aurora-cyan" /> : <UserCircle2 className="w-3 h-3 text-zinc-400" />}
                  <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                    {isEng ? `${t("chat.engineer")} · ${m.sender_name}` : m.sender_name} · {time(m.created_at)}
                  </span>
                </div>
                <p className="text-sm text-zinc-100 whitespace-pre-wrap break-words">{m.text}</p>
                {role === "admin" && m.sender_role === "client" && (clientLang || "").toLowerCase() !== "fr" && (
                  <AutoTranslateFR text={m.text} msgId={m.id} source="order" cachedFr={m.text_fr} cachedIsFr={m.is_fr} cachedLang={m.src_lang} />
                )}
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {role === "admin" ? (
        <div className="flex items-end gap-2">
          <textarea
            data-testid="chat-input"
            rows={1} value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={t("chat.writeMsg")}
            className="flex-1 resize-none bg-black/50 border border-white/10 text-white px-3 py-2.5 rounded-sm text-sm focus:border-aurora-cyan focus:outline-none"
          />
          <AiComposeTools text={text} onApply={setText} clientLang={clientLang} disabled={sending} />
          <button data-testid="chat-send-btn" onClick={send} disabled={sending || !text.trim()}
            className="shrink-0 h-[42px] px-4 bg-aurora-cyan text-white font-display font-bold uppercase tracking-wider text-sm rounded-sm flex items-center gap-2 disabled:opacity-50 transition-colors">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3 p-3 rounded-sm border border-white/10 bg-white/5" data-testid="chat-readonly-note">
          <span className="font-mono text-[10px] text-zinc-400 leading-snug">{t("chat.replyViaTicket")}</span>
          <TicketDialog
            orderId={orderId}
            vehicle={vehicle}
            trigger={
              <button data-testid="chat-open-ticket-btn" className="shrink-0 flex items-center gap-2 px-4 py-2.5 border border-aurora-cyan/40 text-aurora-cyan rounded-sm font-display font-bold uppercase tracking-wider text-xs hover:bg-aurora-cyan/10 transition-colors">
                <LifeBuoy className="w-4 h-4" /> {t("chat.openTicket")}
              </button>
            }
          />
        </div>
      )}
    </div>
  );
};
